import axios from "axios";
import { makeApiRequestPromise } from "../../lib/api";
import { BatchUploadResult, Document } from "./types";

/** Compute SHA-256 hex digest of file content. */
async function sha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Upload documents via presigned URL flow:
 * 1. Get upload URL (POST /documents/upload-url)
 * 2. PUT file to presigned URL
 * 3. Confirm (POST /documents/confirm)
 */
export const uploadDocuments = async (
  files: File[],
  documentType: string = "GENERAL_KNOWLEDGE"
): Promise<BatchUploadResult> => {
  const uploaded: BatchUploadResult["uploaded"] = [];
  const skipped: BatchUploadResult["skipped"] = [];

  for (const file of files) {
    try {
      const uploadUrlResp = (await makeApiRequestPromise({
        path: "/api/v1/context/documents/upload-url",
        method: "POST",
        body: {},
      })) as { upload_url: string; key: string };

      const contentHash = await sha256Hex(file);

      const putResp = await fetch(uploadUrlResp.upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "application/octet-stream" },
      });
      if (!putResp.ok) {
        throw new Error(`Upload failed: ${putResp.status} ${putResp.statusText}`);
      }

      const confirmResp = (await makeApiRequestPromise({
        path: "/api/v1/context/documents/confirm",
        method: "POST",
        body: {
          filename: file.name,
          content_hash: contentHash,
          key: uploadUrlResp.key,
          document_type: documentType,
        },
      })) as { document_id: string; status: string };

      uploaded.push({
        document_id: confirmResp.document_id,
        filename: file.name,
        status: confirmResp.status,
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.detail) {
        skipped.push({ filename: file.name, reason: err.response.data.detail });
      } else {
        const msg = err instanceof Error ? err.message : String(err);
        skipped.push({ filename: file.name, reason: msg });
      }
    }
  }

  return { uploaded, skipped };
};

export const listDocuments = async (): Promise<Document[]> => {
  const response = await makeApiRequestPromise({
    path: "/api/v1/context/documents",
    method: "GET",
  });
  const data = response as { documents: Document[] };
  return data.documents ?? [];
};

export const deleteDocument = async (documentId: string): Promise<void> => {
  try {
    await makeApiRequestPromise({
      path: `/api/v1/context/documents/${documentId}`,
      method: "DELETE",
    });
  } catch (error: any) {
    console.error("Failed to delete document:", error);
    throw new Error(
      error?.response?.data?.detail || error?.message || "Failed to delete document"
    );
  }
};
